// OurProcess.js
// "use client"; // ← uncomment if you're using Next.js App Router

import React from "react";
import { FileText, Video, Users, PlaneTakeoff, Sparkles } from "lucide-react";

const steps = [
  {
    id: "01",
    title: "Apply",
    icon: FileText,
    description:
      "Submit your application through our simple form. We review every application carefully.",
    duration: "< 5 min",
  },
  {
    id: "02",
    title: "Initial Chat",
    icon: Video,
    description:
      "A friendly 30-minute video call with our team to get to know each other.",
    duration: "30 min",
  },
  {
    id: "03",
    title: "Team Interview",
    icon: Users,
    description:
      "Meet the team you’ll be working with. Show us your skills through practical discussions.",
    duration: "1–2 hours",
  },
  {
    id: "04",
    title: "Offer",
    icon: Sparkles,
    description:
      "If it’s a match, you’ll receive an offer within 48 hours. Simple as that.",
    duration: "2 days",
  },
];

const faqTags = [
  "Remote work policy",
  "Visa sponsorship",
  "Interview prep",
  "Timeline questions",
];

export default function OurProcess() {
  return (
    <section className="w-full bg-white py-20 px-4 lg:px-16">
      {/* Heading block */}
      <div className="max-w-4xl mx-auto text-center">
        <span className="carrers_Ourprocess">Our Process</span>

        <h2 className="carrers_Ourprocess-heading">Simple & Transparent</h2>

        <p className="OurProcess_para">
          We respect your time. Our hiring process is straightforward, human,
          and designed to be completed in 1–2 weeks.
        </p>
      </div>

      {/* Steps + connector line */}
      <div className="relative max-w-6xl mx-auto mt-16">
        {/* Horizontal connector line (behind cards) */}
        <div className="pointer-events-none absolute inset-x-10 top-[54%] hidden lg:block">
          <div className="mx-16 border-t border-blue-100" />
        </div>

        <div className="relative grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.id}
                className="relative flex flex-col rounded-[24px] border-2 border-[#E5F0FA] bg-white px-8 py-8 shadow-[0_20px_50px_rgba(15,23,42,0.04)] hover:shadow-xl hover:border-[#2A9CD7]/30 duration-300"
              >
                {/* number pill on top */}
                <div className="absolute -top-4 left-8 rounded-full bg-gradient-to-r from-[#093A5B] to-[#2A9CD7] px-4 py-1 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(9,58,91,0.35)]">
                  {step.id}
                </div>

                {/* icon */}
                <div className="mt-4 mb-6 flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-50">
                  <Icon className="h-7 w-7 text-[#2A9CD7]" />
                </div>

                {/* title + dot */}
                <div className="mb-2 flex items-center gap-2">
                  <h3 className="text-[24px] font-semibold text-[#093A5B]">
                    {step.title}
                  </h3>
                  <span className="inline-block h-2 w-2 rounded-full bg-[#2A9CD7] shadow-[0_0_0_6px_rgba(42,156,215,0.18)]" />
                </div>

                {/* description */}
                <p className="mb-5 text-sm leading-relaxed text-slate-600">
                  {step.description}
                </p>

                {/* duration pill at bottom */}
                <div className="mt-auto w-[90px] rounded-full bg-blue-50 px-4 py-1 text-[11px] font-medium text-[#2A9CD7]">
                  {step.duration}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Have Questions? block */}
      <div className="mt-20">
        <div className="mx-auto max-w-5xl rounded-[32px] bg-[#F5F7FB] px-6 md:px-12 py-10 text-center shadow-[0_18px_45px_rgba(15,23,42,0.03)]">
          <h3 className="text-2xl md:text-3xl font-semibold text-[#093A5B]">
            Have Questions?
          </h3>

          <p className="mt-3 text-base md:text-lg text-slate-600">
            We&apos;re here to help. Reach out to our talent team at{" "}
            <a
              href="mailto:talent@simplifyte.com"
              // className="font-semibold text-[#2A9CD7] underline-offset-4 hover:underline"
              className="relative inline-flex items-center font-semibold tracking-wide align-middle text-base
 text-center border-none after:content-[''] after:absolute after:h-px after:w-0 hover:after:w-full 
 after:end-0 hover:after:end-auto after:bottom-0 after:start-0 after:duration-500 text-indigo-600
 hover:text-indigo-600 after:bg-indigo-600 duration-500 -mt-[4px]"
            >
              {/* careers@simplifyte.com */}
              talent@simplifyte.com
            </a>
            .
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {faqTags.map((tag) => (
              <button
                key={tag}
                type="button"
                className="rounded-full border border-blue-100 bg-white px-4 py-2 text-sm md:text-sm font-medium text-[#093A5B] shadow-sm hover:border-[#2A9CD7] hover:text-[#2A9CD7] transition-colors"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
